import React from 'react';
import './Antrag.css';

interface EducationDataFormProps {
    formData: {
        universityName: string | null;
        universityStreet: string | null;
        universityStreetNumber: string | null;
        universityZip: string | null;
        universityCity: string | null;
        universityCountry: string | null;
        fieldOfStudy: string | null;
        academicDegree: string | null;
        freshmanYear: string | null;
        finalYear: string | null;
        regularStudyTime: string | null;
        studyTimeReason: string | null;
        hasThesis: boolean | null;
    };
    handleChange: (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => void;
    handleHasThesisChange: (value: boolean) => void;
    countries: string[];
    isViewOnly: boolean;
    showValidation: boolean;
}

const EducationDataForm: React.FC<EducationDataFormProps> = ({ formData, handleChange, handleHasThesisChange, countries, isViewOnly, showValidation }) => {

    const generateYearOptions = (start: number, end: number) => {
        const years = [];
        for (let year = start; year <= end; year++) {
            years.push(year);
        }
        return years;
    };
    const years = generateYearOptions(1980, new Date().getFullYear());
    const finalYearOptions = generateYearOptions(Number(formData.freshmanYear) || 1980, new Date().getFullYear() + 5);

    return (
        <div>
            <div className='antrag-ausbildung-header'>
                Studienabschluss
            </div>
            <div className='antrag-ausbildung'>
                <div>
                    <h5>Details zur Hochschule</h5>
                    <p>Bitte geben Sie die folgenden Informationen zur Hochschule an, die Sie besucht haben.</p>
                </div>
                <div>
                    <div className='form-group-ausbildung'>
                        <label htmlFor='universityName' className='form-label'>
                            Name der Hochschule
                        </label>
                        <input
                            id='universityName'
                            tabIndex={isViewOnly ? -1 : 0}
                            className={`${showValidation && !formData.universityName ? ' error' : ''}`}
                            type='text'
                            name='universityName'
                            maxLength={60}
                            value={formData.universityName ?? ''}
                            onChange={handleChange}
                        />
                    </div>
                    <div className='form-group-ausbildung'>
                        <label htmlFor='universityStreet' className='form-label'>
                            Straße
                        </label>
                        <input
                            id='universityStreet'
                            tabIndex={isViewOnly ? -1 : 0}
                            className={`${showValidation && !formData.universityStreet ? ' error' : ''}`}
                            type='text'
                            name='universityStreet'
                            maxLength={50}
                            value={formData.universityStreet ?? ''}
                            onChange={handleChange}
                        />
                    </div>
                    <div className='form-group-ausbildung'>
                        <label className='form-label' htmlFor='universityStreetNumber'>
                            Hausnummer
                        </label>
                        <input
                            id='universityStreetNumber'
                            tabIndex={isViewOnly ? -1 : 0}
                            className={`${showValidation && !formData.universityStreetNumber ? ' error' : ''}`}
                            type='tel'
                            maxLength={8}
                            name='universityStreetNumber'
                            value={formData.universityStreetNumber ?? ''}
                            onChange={handleChange}
                        />
                    </div>
                </div>
                <div>
                    <div className='form-group-ausbildung'>
                        <label className='form-label' htmlFor='universityZip'>
                            Postleitzahl
                        </label>
                        <input
                            id='universityZip'
                            tabIndex={isViewOnly ? -1 : 0}
                            className={`${showValidation && !formData.universityZip ? ' error' : ''}`}
                            type='tel'
                            maxLength={10}
                            name='universityZip'
                            value={formData.universityZip ?? ''}
                            onChange={handleChange}
                        />
                    </div>
                    <div className='form-group-ausbildung'>
                        <label className='form-label' htmlFor='universityCity'>
                            Ort
                        </label>
                        <input
                            id='universityCity'
                            tabIndex={isViewOnly ? -1 : 0}
                            className={`${showValidation && !formData.universityCity ? ' error' : ''}`}
                            type='text'
                            name='universityCity'
                            maxLength={50}
                            value={formData.universityCity ?? ''}
                            onChange={handleChange}
                        />
                    </div>
                    <div className='form-group-ausbildung'>
                        <label className='form-label' htmlFor='universityCountry'>
                            Land
                        </label>
                        <select
                            id='universityCountry'
                            tabIndex={isViewOnly ? -1 : 0}
                            className={`form-control form-select${showValidation && !formData.universityCountry ? ' error' : ''}`}
                            style={{ height: '46px', border: '1px solid #cccccc' }}
                            title='Wählen Sie das Land aus, in dem sich die Hochschule befindet'
                            name='universityCountry'
                            value={formData.universityCountry ?? ''}
                            onChange={handleChange}
                        >
                            <option value="" disabled>Bitte auswählen</option>
                            {countries.map((country, index) => (
                                <option key={index} value={country}>
                                    {country}
                                </option>
                            ))}
                        </select>
                    </div>
                </div>
            </div>
            <div className='antrag-ausbildung' style={{ marginTop: '20px' }}>
                <div>
                    <h5>Details zur akademischen Ausbildung</h5>
                    <p>Bitte geben Sie die folgenden Informationen über Ihren akademischen Abschluss an.</p>
                </div>
                <div>
                    <div className='form-group-ausbildung'>
                        <label className='form-label' htmlFor='fieldOfStudy'>
                            Studienrichtung
                        </label>
                        <input
                            id='fieldOfStudy'
                            tabIndex={isViewOnly ? -1 : 0}
                            className={`${showValidation && !formData.fieldOfStudy ? ' error' : ''}`}
                            type='text'
                            name='fieldOfStudy'
                            value={formData.fieldOfStudy ?? ''}
                            onChange={handleChange}
                            maxLength={50}
                        />
                    </div>
                    <div className='form-group-ausbildung'>
                        <label className='form-label' htmlFor='academicDegree'>
                            Akademischer Grad (falls vorhanden)
                        </label>
                        <input
                            id='academicDegree'
                            tabIndex={isViewOnly ? -1 : 0}
                            type='text'
                            name='academicDegree'
                            value={formData.academicDegree ?? ''}
                            onChange={handleChange}
                            maxLength={50}
                        />
                    </div>
                </div>
                <div>
                    <div>
                        <div className='form-group-ausbildung'>
                            <label htmlFor='freshmanYear' className='form-label'>
                                Studienbeginn
                            </label>
                            <select
                                id='freshmanYear'
                                tabIndex={isViewOnly ? -1 : 0}
                                className={`${showValidation && !formData.freshmanYear ? ' error' : ''}`}
                                name='freshmanYear'
                                value={formData.freshmanYear ?? ''}
                                onChange={handleChange}
                                style={{ cursor: 'pointer' }}
                            >
                                <option value='' disabled>Bitte auswählen</option>
                                {years.map((year) => (
                                    <option key={year} value={year}>
                                        {year}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>
                    <div className='form-group-ausbildung'>
                        <label className='form-label' htmlFor='finalYear'>
                            Studienabschluss
                        </label>
                        <select
                            id='finalYear'
                            tabIndex={isViewOnly ? -1 : 0}
                            className={`${showValidation && !formData.finalYear ? ' error' : ''}`}
                            name='finalYear'
                            value={formData.finalYear ?? ''}
                            onChange={handleChange}
                            style={{ cursor: 'pointer' }}
                        >
                            <option value='' disabled>Bitte auswählen</option>
                            {finalYearOptions.map((year) => (
                                <option key={year} value={year}>
                                    {year}
                                </option>
                            ))}
                        </select>
                    </div>
                </div>
                <div />
                <div className='form-group-ausbildung'>
                    <label className='form-label' htmlFor='regularStudyTime'>
                        Regelstudienzeit
                    </label>
                    <select
                        id='regularStudyTime'
                        tabIndex={isViewOnly ? -1 : 0}
                        className={`${showValidation && !formData.regularStudyTime ? ' error' : ''}`}
                        name='regularStudyTime'
                        style={{ cursor: 'pointer' }}
                        value={formData.regularStudyTime ?? ''}
                        onChange={handleChange}
                    >
                        <option value='' disabled>Bitte auswählen</option>
                        <option value='1'>1 Jahr</option>
                        <option value='2'>2 Jahre</option>
                        <option value='3'>3 Jahre</option>
                        <option value='4'>4 Jahre</option>
                        <option value='5'>5 Jahre</option>
                    </select>
                </div>
                {formData.finalYear && formData.freshmanYear && formData.regularStudyTime &&
                    (Number(formData.finalYear) - Number(formData.freshmanYear)) > Number(formData.regularStudyTime) ? (
                    <div className='form-group-ausbildung'>
                        <label className='form-label' htmlFor='studyTimeReason'>
                            Grund für die Überschreitung der Regelstudienzeit?
                        </label>
                        <textarea
                            id='studyTimeReason'
                            tabIndex={isViewOnly ? -1 : 0}
                            className='input-field'
                            name='studyTimeReason'
                            maxLength={200}
                            placeholder='Bitte geben Sie einen Grund an'
                            rows={5}
                            value={formData.studyTimeReason ?? ''}
                            onChange={handleChange}
                        >
                        </textarea>
                    </div>
                ) : <div />}
                <div>
                    <h5 style={{ fontSize: '18px' }}>Haben Sie im Rahmen des Studiums eine wissenschaftliche Abschlussarbeit geschrieben?</h5>
                </div>
                <div style={{ display: 'flex' }} className={`${showValidation && formData.hasThesis === null ? ' error' : ''}`}>
                    <label className='checkbox-label' >
                        <input
                            type="checkbox"
                            name="hasThesis"
                            checked={formData.hasThesis === true}
                            onChange={() => handleHasThesisChange(true)}
                        />Ja
                        <span
                            className='checkmark'
                            tabIndex={isViewOnly ? -1 : 0}
                            role="checkbox"
                            aria-checked={formData.hasThesis === true}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                    handleHasThesisChange(true);
                                }
                            }}
                        ></span>
                    </label>
                    <label className='checkbox-label'>
                        <input
                            type="checkbox"
                            name="hasThesis"
                            checked={formData.hasThesis === false}
                            onChange={() => handleHasThesisChange(false)}
                        />
                        Nein
                        <span
                            className='checkmark'
                            tabIndex={isViewOnly ? -1 : 0}
                            role="checkbox"
                            aria-checked={formData.hasThesis === false}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                    handleHasThesisChange(false);
                                }
                            }}
                        ></span>
                    </label>
                </div>
            </div>
        </div>
    )
}

export default EducationDataForm;